import { defineStore } from "pinia";
import { reactive, computed } from "vue";

import TeamInvitationsApi from "@Modules/teams/Api/TeamInvitationsApi";
import ShowModal from "@Helpers/functions/showModal.js";
import InvitationPopup from "@Modules/teams/components/InvitationPopup.vue";
import { TeamInvitationWithSource } from "@Modules/teams/Data/Models/TeamInvitationModel";
import useTranslation from "@Composables/useTranslation";

export const XTeamInvitationsStore = defineStore("x-team-invitations", () => {
    const api = TeamInvitationsApi();
    const { __ } = useTranslation();

    //
    // State
    const state = reactive({
        isPopupVisible: false,
    });

    //
    // Getters
    const invitations = computed<TeamInvitationWithSource[]>(() =>
        api.invitations.value.map((invitation) => ({ ...invitation, source: "team" }))
    );
    const loading = computed(() => api.loading.value);
    const hasInvitations = computed(() => invitations.value.length > 0);

    const currentInvitation = computed(() => invitations.value[0] || null);

    //
    // Actions
    async function loadInvitations(): Promise<void> {
        await api.fetchInvitations();
    }

    function showNextInvitationPopup(): void {
        const invitation = currentInvitation.value;
        if (!invitation || state.isPopupVisible) {
            return;
        }

        state.isPopupVisible = true;
        ShowModal(InvitationPopup, {
            title: __("team:team_invitation"),
            entityName: invitation.team?.name || "",
            inviterName: invitation.inviter.name,
            roleName: invitation.role,
            acceptInvitation: () => api.acceptInvitation(invitation.id),
            rejectInvitation: () => api.rejectInvitation(invitation.id),
            notifyClosed: (succeeded: boolean) => {
                state.isPopupVisible = false;
                if (succeeded) {
                    showNextInvitationPopup();
                }
            },
        });
    }

    async function initializeAndShowPopup(): Promise<void> {
        await loadInvitations();
        showNextInvitationPopup();
    }

    function reset(): void {
        state.isPopupVisible = false;
        api.reset();
    }

    return {
        invitations,
        loading,
        hasInvitations,
        currentInvitation,
        loadInvitations,
        showNextInvitationPopup,
        initializeAndShowPopup,
        reset,
        acceptInvitation: api.acceptInvitation,
        rejectInvitation: api.rejectInvitation,
        setRouter: api.setRouter,
    };
});
